import { useState, useMemo, useRef, useEffect } from "react"
import { PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import type { Adset } from "../types"

interface CustomAdset {
  id: string
  title: string
  products: string[]
}

interface CustomStructureBuilderProps {
  onComplete: (adsets: Adset[]) => void
  onBack: () => void
}

const CUBE_PREFIX = "company_web_analytics_page_path_report."

function getLast60DaysRange(): [string, string] {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - 60)
  return [start.toISOString().split("T")[0], end.toISOString().split("T")[0]]
}

export default function CustomStructureBuilder({
  onComplete,
  onBack,
}: CustomStructureBuilderProps) {
  const [adsets, setAdsets] = useState<CustomAdset[]>([
    { id: crypto.randomUUID(), title: "", products: [] },
  ])
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({})
  const [focusedAdsetId, setFocusedAdsetId] = useState<string | null>(null)
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Pre-fetch products with >10 first_orders in last 60 days
  const cubeQuery = useMemo(
    () => ({
      dimensions: [`${CUBE_PREFIX}title`],
      measures: [`${CUBE_PREFIX}first_orders`],
      filters: [
        {
          member: `${CUBE_PREFIX}first_orders`,
          operator: "gt" as const,
          values: ["10"],
        },
      ],
      timeDimensions: [
        {
          dimension: `${CUBE_PREFIX}md_date`,
          dateRange: getLast60DaysRange(),
        },
      ],
      order: [[`${CUBE_PREFIX}first_orders`, "desc"]] as [string, "desc"][],
      limit: 500,
    }),
    [],
  )

  const { resultSet, isLoading } = useCubeQuery(cubeQuery)

  const allProducts = useMemo<string[]>(() => {
    if (!resultSet) return []
    const rows = resultSet.rawData() as Record<string, string>[]
    return rows
      .map(row => row[`${CUBE_PREFIX}title`])
      .filter((title): title is string => Boolean(title))
  }, [resultSet])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (focusedAdsetId) {
        const target = e.target as Node
        // Guard against elements removed from DOM mid-event (e.g. dropdown items after re-render)
        if (!document.contains(target)) return
        const ref = dropdownRefs.current[focusedAdsetId]
        if (ref && !ref.contains(target)) {
          setFocusedAdsetId(null)
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [focusedAdsetId])

  const getFilteredProducts = (adsetId: string) => {
    const term = (searchTerms[adsetId] ?? "").toLowerCase()
    const adset = adsets.find(a => a.id === adsetId)
    const selected = new Set(adset?.products ?? [])
    return allProducts.filter(
      p => !selected.has(p) && p.toLowerCase().includes(term),
    )
  }

  const handleAddAdset = () => {
    setAdsets(prev => [
      ...prev,
      { id: crypto.randomUUID(), title: "", products: [] },
    ])
  }

  const handleRemoveAdset = (id: string) => {
    if (adsets.length <= 1) return
    setAdsets(prev => prev.filter(a => a.id !== id))
    setSearchTerms(prev => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const handleTitleChange = (id: string, title: string) => {
    setAdsets(prev => prev.map(a => (a.id === id ? { ...a, title } : a)))
  }

  const handleAddProduct = (adsetId: string, product: string) => {
    setAdsets(prev =>
      prev.map(a =>
        a.id === adsetId ? { ...a, products: [...a.products, product] } : a,
      ),
    )
  }

  const handleRemoveProduct = (adsetId: string, product: string) => {
    setAdsets(prev =>
      prev.map(a =>
        a.id === adsetId
          ? { ...a, products: a.products.filter(p => p !== product) }
          : a,
      ),
    )
  }

  const handleComplete = () => {
    const result: Adset[] = adsets
      .filter(a => a.title.trim())
      .map(a => ({
        id: a.id,
        title: a.title.trim(),
        description: a.products.length
          ? `Products: ${a.products.join(", ")}`
          : undefined,
      }))
    onComplete(result)
  }

  const hasValidAdsets = adsets.some(a => a.title.trim())

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-basic-white mb-1">
          Build Your Own Structure
        </h2>
        <p className="text-sm text-basic-light/60">
          Name your adsets and assign products to each one.
          {isLoading && " Loading products..."}
        </p>
      </div>

      <div className="space-y-4">
        {adsets.map((adset, index) => (
          <div
            key={adset.id}
            className="bg-system-gray-900/50 border border-system-gray-700/50 rounded-lg p-4 space-y-3"
          >
            {/* Adset header: title input + delete */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-basic-light/40 shrink-0">
                Adset {index + 1}
              </span>
              <input
                type="text"
                placeholder="Adset name..."
                value={adset.title}
                onChange={e => handleTitleChange(adset.id, e.target.value)}
                className="input input-bordered input-sm flex-1 bg-system-gray-900/50 border-system-gray-700/50 text-basic-white placeholder:text-basic-light/30"
              />
              <button
                className="btn btn-ghost btn-sm btn-square text-error"
                onClick={() => handleRemoveAdset(adset.id)}
                disabled={adsets.length <= 1}
                title={
                  adsets.length <= 1
                    ? "At least one adset is required"
                    : "Remove adset"
                }
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Product search */}
            <div
              className="relative"
              ref={el => {
                dropdownRefs.current[adset.id] = el
              }}
            >
              <input
                type="text"
                placeholder={
                  isLoading
                    ? "Loading products..."
                    : "Search products to add..."
                }
                value={searchTerms[adset.id] ?? ""}
                onChange={e =>
                  setSearchTerms(prev => ({
                    ...prev,
                    [adset.id]: e.target.value,
                  }))
                }
                onFocus={() => setFocusedAdsetId(adset.id)}
                disabled={isLoading}
                className="input input-bordered input-sm w-full bg-system-gray-900/50 border-system-gray-700/50 text-basic-white placeholder:text-basic-light/30"
              />
              {focusedAdsetId === adset.id &&
                (searchTerms[adset.id] ?? "").length > 0 && (
                  <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto bg-system-gray-900 border border-system-gray-700/50 rounded-lg shadow-lg">
                    {getFilteredProducts(adset.id).length === 0 ? (
                      <div className="px-3 py-2 text-xs text-basic-light/40">
                        No matching products
                      </div>
                    ) : (
                      getFilteredProducts(adset.id)
                        .slice(0, 20)
                        .map(product => (
                          <button
                            key={product}
                            className="w-full text-left px-3 py-2 text-sm text-basic-white hover:bg-system-gray-700/50 transition-colors"
                            onMouseDown={e => {
                              e.preventDefault()
                              handleAddProduct(adset.id, product)
                            }}
                          >
                            {product}
                          </button>
                        ))
                    )}
                  </div>
                )}
            </div>

            {/* Selected products as pills */}
            {adset.products.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {adset.products.map(product => (
                  <span
                    key={product}
                    className="inline-flex items-center gap-1 text-xs bg-system-gray-700/50 text-basic-light/70 px-2 py-1 rounded-full"
                  >
                    {product}
                    <button
                      onClick={() => handleRemoveProduct(adset.id, product)}
                      className="hover:text-error transition-colors"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}

        <button
          className="w-full border-2 border-dashed border-system-gray-700/50 rounded-lg p-4 flex items-center justify-center gap-2 text-sm text-basic-light/50 hover:text-basic-light/80 hover:border-system-gray-700 transition-colors"
          onClick={handleAddAdset}
        >
          <PlusIcon className="w-4 h-4" />
          Add Another Adset
        </button>
      </div>

      <div className="flex justify-between pt-4">
        <button className="btn btn-ghost" onClick={onBack}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
          Back
        </button>
        <button
          className="btn btn-primary"
          disabled={!hasValidAdsets}
          onClick={handleComplete}
        >
          Continue to Review
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
